import { useContentHealth } from "@h/useContentHealth";
import { useIsTouchDevice } from "@h/useResponsive";
import { Popover, PopoverContent, PopoverTrigger } from "@ui/popover";
import ResponsiveSheet from "@ui/responsive-sheet";
import { Tooltip, TooltipContent, TooltipTrigger } from "@ui/tooltip";
import { AlertTriangle } from "lucide-react";
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { HealthPanel } from "./HealthPanel";

export function HealthBell() {
  const { count, isError } = useContentHealth();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const isTouch = useIsTouchDevice();

  // Nothing to fix AND the check succeeded → no indicator (clean header). On a
  // failed check we still show the bell so "0" can't be mistaken for "all good".
  if (count === 0 && !isError) return null;

  const trigger = (onClick?: () => void) => (
    <button
      type="button"
      onClick={onClick}
      aria-label={isError ? t("health.check_failed") : t("health.aria")}
      className="relative inline-flex items-center justify-center size-9 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
    >
      <AlertTriangle
        size={18}
        strokeWidth={1.75}
        className={isError ? "text-muted-foreground" : "text-warning"}
      />
      <span
        className={
          isError
            ? "absolute -top-0.5 -right-0.5 min-w-4 h-4 px-1 rounded-full bg-muted-foreground text-white label-micro font-mono leading-none flex items-center justify-center"
            : "absolute -top-0.5 -right-0.5 min-w-4 h-4 px-1 rounded-full bg-warning-subtle text-warning-strong label-micro font-mono leading-none flex items-center justify-center"
        }
      >
        {isError ? "!" : count > 99 ? "99+" : count}
      </span>
    </button>
  );

  // Icon-only at every width, so the name only ever surfaces on hover.
  const withTip = (node: ReactNode) => (
    <Tooltip>
      <TooltipTrigger asChild>{node}</TooltipTrigger>
      <TooltipContent side="bottom">
        {isError ? t("health.check_failed") : t("health.aria")}
      </TooltipContent>
    </Tooltip>
  );

  if (isTouch) {
    return (
      <>
        {withTip(trigger(() => setOpen(true)))}
        <ResponsiveSheet open={open} onOpenChange={setOpen} hideTitle>
          <HealthPanel onClose={() => setOpen(false)} inSheet />
        </ResponsiveSheet>
      </>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      {withTip(<PopoverTrigger asChild>{trigger()}</PopoverTrigger>)}
      <PopoverContent align="end" className="p-0 w-auto">
        <HealthPanel onClose={() => setOpen(false)} />
      </PopoverContent>
    </Popover>
  );
}
